import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { classNames } from "primereact/utils";
import { Controller, useFormContext } from "react-hook-form";
import EmojiPicker, { EmojiClickData, Theme } from "emoji-picker-react";
import styles from "./DefaultFormEmojiField.module.scss";

interface IDefaultFormEmojiFieldProps {
  name: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const CLOSE_ANIMATION_MS = 200;

/**
 * Form field wrapping `emoji-picker-react` as a controlled input
 * for react-hook-form. Stores the selected emoji as a string in the
 * form value. Opens a centred overlay picker on click.
 */
export const DefaultFormEmojiField = ({
  name,
  label,
  required,
  placeholder = "Изберете emoji",
  className,
  disabled,
}: IDefaultFormEmojiFieldProps) => {
  const { control } = useFormContext();
  const [open, setOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const closePicker = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setOpen(false);
      setIsClosing(false);
    }, CLOSE_ANIMATION_MS);
  }, []);

  const closeOnEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closePicker();
      }
    },
    [closePicker]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", closeOnEscape, true);
    return () => document.removeEventListener("keydown", closeOnEscape, true);
  }, [open, closeOnEscape]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const hasValue = !!field.value;

        const handlePick = (data: EmojiClickData) => {
          field.onChange(data.emoji);
          closePicker();
        };

        return (
          <div className={classNames(className, styles.root)}>
            <div
              className={classNames(styles.root_label, {
                [styles.root_label_error]: !!fieldState.error?.message,
              })}
            >
              {label}
              {required ? "*" : ""}
            </div>
            <button
              type="button"
              disabled={disabled}
              onClick={() => setOpen((v) => !v)}
              className={classNames(styles.root_trigger, {
                [styles.root_trigger_empty]: !hasValue,
              })}
            >
              {hasValue ? (
                <span className={styles.root_selected}>{field.value}</span>
              ) : (
                <span className={styles.root_placeholder}>{placeholder}</span>
              )}
            </button>

            {open &&
              createPortal(
                <div
                  className={classNames(styles.picker_overlay, {
                    [styles.picker_overlay_closing]: isClosing,
                  })}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (e.target === e.currentTarget) closePicker();
                  }}
                >
                  <div
                    ref={wrapperRef}
                    className={classNames(styles.picker_wrapper, {
                      [styles.picker_wrapper_closing]: isClosing,
                    })}
                  >
                    <EmojiPicker
                      onEmojiClick={handlePick}
                      theme={Theme.DARK}
                      width="100%"
                      height={560}
                      searchPlaceHolder="Търсене..."
                    />
                  </div>
                </div>,
                document.body
              )}
          </div>
        );
      }}
    />
  );
};
